import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { ref, get, set } from 'firebase/database';
import SEO from '../components/SEO';
import TemplateSelector from '../components/TemplateSelector';
import SaveAsTemplate from '../components/SaveAsTemplate';
import MediaPicker from '../components/MediaPicker';

/* ─────────────────────────────────────────────
   TYPES (unchanged from original)
───────────────────────────────────────────── */
interface Button { id: string; title: string; payload: string; }
interface ListSection { title: string; rows: Button[]; }
interface ResponseItem {
  id: string;
  type: 'text' | 'image' | 'document' | 'buttons' | 'list' | 'dynamic_list' | 'webhook' | 'ask' | 'chain' | 'dynamic_response' | 'order_handler' | 'request_agent';
  content: string;
  caption?: string;
  filename?: string;
  buttons?: Button[];
  chainTrigger?: string;
  chainDelay?: number;
  listSections?: ListSection[];
  apiUrl?: string;
  apiMethod?: 'GET' | 'POST';
  apiHeaders?: { key: string; value: string }[];
  apiBody?: string;
  itemTitlePath?: string;
  itemPayloadPath?: string;
  listTitle?: string;
  orderHandlerConfig?: {
    collectName: boolean; collectPhone: boolean; collectEmail: boolean; collectAddress: boolean;
    askQuantity: boolean; quantityLabel?: string;
    action: 'payment_link' | 'admin_notification' | 'api_call' | 'custom_message';
    paymentLinkUrl?: string; paymentLinkLabel?: string;
    adminPhone?: string; adminMessage?: string;
    apiUrl?: string; apiMethod?: 'GET' | 'POST'; apiBody?: string;
    successMessage?: string; errorMessage?: string;
    customMessage?: string; confirmationMessage?: string;
  };
  dynamicResponseConfig?: {
    sourceListTrigger?: string; apiUrl: string; apiMethod: 'GET' | 'POST';
    apiHeaders?: { key: string; value: string }[];
    apiBody?: string; metaLabel?: string; imagePath?: string;
    titlePath: string; categoryPath?: string; pricePath: string;
    metaPath?: string; descriptionPath: string;
    customFields?: Array<{ id: string; label: string; path: string; emoji?: string }>;
    button1Title?: string; button1Payload?: string;
    button2Title?: string; button2Payload?: string;
    button3Title?: string; button3Payload?: string;
    fallbackMessage?: string;
  };
  webhookMethod?: 'GET' | 'POST';
  webhookBody?: string;
  webhookResponsePath?: string;
  webhookSuccessMessage?: string;
  webhookErrorMessage?: string;
  askVariable?: string;
  askResponses?: ResponseItem[];
}
interface Trigger { trigger: string; responses: ResponseItem[]; }

const getDefaultDynamicResponseConfig = () => ({
  apiUrl: '', apiMethod: 'GET' as const, apiHeaders: [], apiBody: '',
  titlePath: '', pricePath: '', metaPath: '', metaLabel: '',
  descriptionPath: '', imagePath: '', categoryPath: '',
  button1Title: '', button1Payload: '', button2Title: '', button2Payload: '',
  button3Title: '', button3Payload: '', fallbackMessage: '', sourceListTrigger: '',
  customFields: [] as Array<{ id: string; label: string; path: string; emoji?: string }>,
});

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { background: 'var(--bg-page)', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  input: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', appearance: 'none' as const, boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '9px 12px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const },
  card: { background: 'var(--bg-card)', borderRadius: '14px', border: '1px solid var(--bg-element)', padding: '20px', marginBottom: '16px' } as React.CSSProperties,
  respCard: { background: 'var(--bg-sidebar)', borderRadius: '10px', border: '1px solid var(--bg-element)', padding: '16px', marginBottom: '12px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  btnPrimary: { padding: '8px 18px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' } as React.CSSProperties,
  btnDanger: { padding: '6px 12px', background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGhost: { padding: '6px 12px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--bg-element)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' } as React.CSSProperties,
  btnGreen: { padding: '6px 14px', background: '#10b98120', color: '#10b981', border: '1px solid #10b98140', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' } as React.CSSProperties,
};

/* ─────────────────────────────────────────────
   SAVE HELPERS
───────────────────────────────────────────── */
function normalizeResponse(r: any, idx: number): ResponseItem {
  return {
    ...r,
    id: r.id || `resp_${idx}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
    type: r.type || 'text',
    content: r.content || (r as any).message || (r as any).url || '',
    askVariable: r.askVariable || (r as any).variable || r.askVariable,
  };
}

function cleanResponseItem(r: ResponseItem, idx: number = 0): any {
  const clean: any = {
    id: r.id || `resp_${idx}_${Date.now().toString(36)}`,
    type: r.type,
    content: r.content || (r as any).message || (r as any).url || '',
  };
  if (r.caption) clean.caption = r.caption;
  if (r.filename) clean.filename = r.filename;
  if (r.askVariable) clean.askVariable = r.askVariable;
  if (r.chainTrigger) clean.chainTrigger = r.chainTrigger;
  if (r.chainDelay) clean.chainDelay = r.chainDelay;
  if (r.webhookMethod) clean.webhookMethod = r.webhookMethod;
  if (r.webhookBody) clean.webhookBody = r.webhookBody;
  if (r.webhookResponsePath) clean.webhookResponsePath = r.webhookResponsePath;
  if (r.webhookSuccessMessage) clean.webhookSuccessMessage = r.webhookSuccessMessage;
  if (r.webhookErrorMessage) clean.webhookErrorMessage = r.webhookErrorMessage;
  if (r.buttons?.length) clean.buttons = r.buttons.map((b, i) => ({ id: b.id || `btn_${i}`, title: b.title, payload: b.payload }));
  if (r.listSections?.length) clean.listSections = r.listSections.map(s => ({ title: s.title, rows: s.rows.map((row, i) => ({ id: row.id || `row_${i}`, title: row.title, payload: row.payload })) }));
  if (r.askResponses?.length) clean.askResponses = r.askResponses.map((ar, i) => cleanResponseItem(ar, i));
  return clean;
}

/* ─────────────────────────────────────────────
   FLOW DIAGRAM MODAL
───────────────────────────────────────────── */
/* ─────────────────────────────────────────────
   FLOW DIAGRAM MODAL  (replace existing)
───────────────────────────────────────────── */
function FlowDiagramModal({ triggers, botName, onClose }: { triggers: any[]; botName: string; onClose: () => void }) {
  const VP_W = typeof window !== 'undefined' ? window.innerWidth : 1400;

  const TRIG_X = 60;
  const TRIG_W = 200;
  const TRIG_H = 64;

  const RESP_X = TRIG_X + TRIG_W + 160;
  const RESP_W = 230;
  const RESP_H = 52;
  const RESP_GAP = 10;

  const PILL_H = 24;
  const PILL_GAP = 6;

  const ROW_PAD_TOP = 28;
  const ROW_PAD_BOTTOM = 40;
  const PAD_TOP = 48;

  const LANE_START_X = RESP_X + RESP_W + 50;
  const LANE_STEP = 22;

  const typeColor: Record<string, string> = {
    text: '#10b981', image: '#3b82f6', document: '#8b5cf6',
    buttons: '#f59e0b', list: '#6366f1', ask: '#ec4899',
    chain: '#14b8a6', webhook: '#ef4444',
    dynamic_list: '#0ea5e9', dynamic_response: '#f97316', order_handler: '#84cc16',
  };
  const typeIcon: Record<string, string> = {
    text: '💬', buttons: '🔘', list: '📋', ask: '❓', image: '🖼️',
    document: '📄', chain: '⛓️', webhook: '🌐',
    dynamic_list: '📡', dynamic_response: '🔗', order_handler: '🛒',
  };

  const triggerSet = new Set(triggers.map(t => t.trigger.toLowerCase()));

  function getPills(resp: any): Array<{ label: string; payload: string }> {
    const out: Array<{ label: string; payload: string }> = [];
    if (resp.type === 'buttons') {
      resp.buttons?.forEach((b: any) => {
        if (b.payload) out.push({ label: b.title, payload: b.payload.toLowerCase() });
      });
    }
    if (resp.type === 'list') {
      resp.listSections?.forEach((s: any) =>
        s.rows?.slice(0, 8).forEach((row: any) => {
          if (row.payload) out.push({ label: row.title, payload: row.payload.toLowerCase() });
        })
      );
    }
    if (resp.type === 'chain' && resp.chainTrigger) {
      out.push({ label: `→ ${resp.chainTrigger}`, payload: resp.chainTrigger.toLowerCase() });
    }
    return out;
  }

  function rowContentHeight(responses: any[]): number {
    let h = 0;
    responses.forEach((r, i) => {
      if (i > 0) h += RESP_GAP;
      h += RESP_H;
      const pills = getPills(r);
      if (pills.length) h += 8 + pills.length * (PILL_H + PILL_GAP);
    });
    return Math.max(TRIG_H, h);
  }

  type Row = {
    trigger: any; tIdx: number; y: number;
    contentH: number; totalH: number; responses: any[];
  };

  const rows: Row[] = [];
  let curY = PAD_TOP;
  for (let tIdx = 0; tIdx < triggers.length; tIdx++) {
    const responses = triggers[tIdx].responses || [];
    const contentH = rowContentHeight(responses);
    const totalH = ROW_PAD_TOP + contentH + ROW_PAD_BOTTOM;
    rows.push({ trigger: triggers[tIdx], tIdx, y: curY, contentH, totalH, responses });
    curY += totalH;
  }

  const svgHeight = curY + 60;

  function triggerTopY(payload: string): number | null {
    const row = rows.find(r => r.trigger.trigger.toLowerCase() === payload);
    return row ? row.y + ROW_PAD_TOP : null;
  }

  function triggerMidY(payload: string): number | null {
    const row = rows.find(r => r.trigger.trigger.toLowerCase() === payload);
    return row ? row.y + ROW_PAD_TOP + TRIG_H / 2 : null;
  }

  type ArrowDef = {
    fromY: number;
    destTopY: number;
    destMidY: number;
    laneX: number;
  };

  const arrows: ArrowDef[] = [];
  let globalLaneIdx = 0;

  rows.forEach(({ responses, y }) => {
    let ry = y + ROW_PAD_TOP;
    responses.forEach((resp: any) => {
      ry += RESP_H;
      const pills = getPills(resp);
      if (pills.length) {
        ry += 8;
        pills.forEach((pill) => {
          if (triggerSet.has(pill.payload)) {
            const destTop = triggerTopY(pill.payload);
            const destMid = triggerMidY(pill.payload);
            if (destTop !== null && destMid !== null) {
              const laneX = LANE_START_X + globalLaneIdx * LANE_STEP;
              globalLaneIdx++;
              arrows.push({
                fromY: ry + PILL_H,   // exit from bottom of pill
                destTopY: destTop - 6, // arrive just above trigger card top
                destMidY: destMid,
                laneX,
              });
            }
          }
          ry += PILL_H + PILL_GAP;
        });
      } else {
        ry += RESP_GAP;
      }
    });
  });

  const trigMidX = TRIG_X + TRIG_W / 2;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg-page)', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 32px', borderBottom: '1px solid var(--bg-element)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ fontSize: '22px' }}>🗺️</span>
            <div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '17px' }}>{botName} — Flow map</div>
              <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginTop: '3px' }}>
                {triggers.length} triggers · Arrows show where buttons &amp; lists lead
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--bg-element)', border: 'none', color: 'var(--text-secondary)',
              borderRadius: '8px', padding: '9px 20px', cursor: 'pointer',
              fontSize: '14px', fontWeight: '600',
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <svg width={VP_W} height={svgHeight} style={{ display: 'block' }}>
            <defs>
              <marker id="flarr" viewBox="0 0 10 10" refX="8" refY="5"
                markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M2 1L8 5L2 9" fill="none" stroke="#4a5568"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </marker>
              <marker id="flarr-link" viewBox="0 0 10 10" refX="8" refY="5"
                markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M2 1L8 5L2 9" fill="none" stroke="#10b981"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </marker>
            </defs>

            {/* ── PASS 1: arrows behind everything ── */}
            {arrows.map((arrow, i) => {
              const { fromY, destTopY, destMidY, laneX } = arrow;
              const pillExitX = RESP_X + RESP_W + 8;

              // Route:
              // 1. Exit pill bottom → go right to own lane
              // 2. Travel up/down lane to just above destination trigger top
              // 3. Go left from lane across to trigger center X
              // 4. Drop down into the trigger card from the top
              const d = [
                `M ${pillExitX} ${fromY}`,
                `L ${laneX} ${fromY}`,
                `L ${laneX} ${destTopY}`,
                `L ${trigMidX} ${destTopY}`,
                `L ${trigMidX} ${destMidY - 4}`,
              ].join(' ');

              return (
                <path
                  key={`arrow-${i}`}
                  d={d}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  markerEnd="url(#flarr-link)"
                  opacity={0.75}
                />
              );
            })}

            {/* ── PASS 2: rows, cards, pills — all on top of arrows ── */}
            {rows.map(({ trigger, tIdx, y, responses, totalH }) => {
              const contentY = y + ROW_PAD_TOP;
              const trigMidY = contentY + TRIG_H / 2;

              // Collect which triggers in this row are destinations so we can show entry dot
              const isDestination = arrows.some(a => Math.abs(a.destMidY - trigMidY) < 2);

              return (
                <g key={tIdx}>
                  {/* Alternating row tint */}
                  {tIdx % 2 === 0 && (
                    <rect x={0} y={y} width={VP_W} height={totalH} fill="#ffffff03" />
                  )}

                  {/* Trigger card — double rect for opaque cover */}
                  <rect x={TRIG_X} y={contentY} width={TRIG_W} height={TRIG_H} rx={12} fill="#0f1117" />
                  <rect x={TRIG_X} y={contentY} width={TRIG_W} height={TRIG_H} rx={12} fill="#12111A" stroke="#5B4FE8" strokeWidth={1.5} />
                  <text
                    x={TRIG_X + TRIG_W / 2} y={contentY + 20}
                    textAnchor="middle" fill="#5B4FE8" fontSize={10} fontWeight={700}
                    letterSpacing="0.1em"
                    style={{ textTransform: 'uppercase', fontFamily: 'inherit' }}
                  >
                    trigger
                  </text>
                  <text
                    x={TRIG_X + TRIG_W / 2} y={contentY + 44}
                    textAnchor="middle" fill="#e2e8f0" fontSize={17} fontWeight={700}
                    style={{ fontFamily: 'monospace' }}
                  >
                    {trigger.trigger}
                  </text>

                  {/* Entry dot on top of trigger where arrows arrive */}
                  {isDestination && (
                    <circle
                      cx={trigMidX}
                      cy={contentY}
                      r={5}
                      fill="#10b981"
                      stroke="#0f1117"
                      strokeWidth={2}
                    />
                  )}

                  {/* Trigger → first response connector */}
                  {responses.length > 0 && (
                    <line
                      x1={TRIG_X + TRIG_W} y1={trigMidY}
                      x2={RESP_X - 4} y2={trigMidY}
                      stroke="#4a5568" strokeWidth={1.5}
                      markerEnd="url(#flarr)"
                    />
                  )}

                  {/* Response cards + pills */}
                  {(() => {
                    const els: React.ReactNode[] = [];
                    let ry = contentY;

                    responses.forEach((resp: any, rIdx: number) => {
                      const color = typeColor[resp.type] || '#64748b';
                      const icon = typeIcon[resp.type] || '💬';
                      const pills = getPills(resp);

                      if (rIdx > 0) {
                        els.push(
                          <line
                            key={`conn-${rIdx}`}
                            x1={RESP_X + RESP_W / 2} y1={ry - RESP_GAP}
                            x2={RESP_X + RESP_W / 2} y2={ry}
                            stroke="#2d3748" strokeWidth={1} strokeDasharray="3 3"
                          />
                        );
                      }

                      // Response card with opaque backdrop
                      els.push(
                        <g key={`card-${rIdx}`}>
                          <rect x={RESP_X} y={ry} width={RESP_W} height={RESP_H} rx={10} fill="#0f1117" />
                          <rect x={RESP_X} y={ry} width={RESP_W} height={RESP_H} rx={10} fill={color + '12'} stroke={color + '45'} strokeWidth={1} />
                          <rect x={RESP_X} y={ry} width={4} height={RESP_H} rx={2} fill={color} />
                          <text
                            x={RESP_X + 18} y={ry + 20} fill={color} fontSize={11} fontWeight={700}
                            style={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'inherit' }}
                          >
                            {icon} {resp.type}
                          </text>
                          <text
                            x={RESP_X + 18} y={ry + 38} fill="#94a3b8" fontSize={12}
                            style={{ fontFamily: 'inherit' }}
                          >
                            {(resp.content || '').slice(0, 30)}{(resp.content || '').length > 30 ? '…' : ''}
                          </text>
                        </g>
                      );

                      ry += RESP_H;

                      if (pills.length) {
                        ry += 8;
                        pills.forEach((pill, pIdx) => {
                          const isLinked = triggerSet.has(pill.payload);
                          const pc = isLinked ? '#10b981' : '#64748b';

                          // Pills with opaque backdrop
                          els.push(
                            <g key={`pill-${rIdx}-${pIdx}`}>
                              <rect x={RESP_X + 14} y={ry} width={RESP_W - 28} height={PILL_H} rx={12} fill="#0f1117" />
                              <rect x={RESP_X + 14} y={ry} width={RESP_W - 28} height={PILL_H} rx={12} fill={pc + '18'} stroke={pc + '70'} strokeWidth={1} />
                              <text
                                x={RESP_X + RESP_W / 2} y={ry + PILL_H / 2}
                                textAnchor="middle" dominantBaseline="central"
                                fill={pc} fontSize={11} fontWeight={600}
                                style={{ fontFamily: 'inherit' }}
                              >
                                {pill.label.slice(0, 26)}{pill.label.length > 26 ? '…' : ''}{isLinked ? ' ↗' : ''}
                              </text>
                            </g>
                          );
                          ry += PILL_H + PILL_GAP;
                        });
                      } else {
                        ry += RESP_GAP;
                      }
                    });

                    return els;
                  })()}

                  {/* Row divider */}
                  {tIdx < rows.length - 1 && (
                    <line
                      x1={40} y1={y + totalH}
                      x2={VP_W - 40} y2={y + totalH}
                      stroke="#1e2330" strokeWidth={1}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div style={{
          padding: '12px 32px', borderTop: '1px solid var(--bg-element)',
          display: 'flex', gap: '20px', flexWrap: 'wrap',
          alignItems: 'center', flexShrink: 0, background: '#0c0e14',
        }}>
          {Object.entries(typeIcon).map(([type, icon]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontSize: '12px' }}>{icon}</span>
              <span style={{ fontSize: '11px', color: typeColor[type] || 'var(--text-faint)', fontWeight: '600' }}>{type}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
            <svg width="40" height="12">
              <line x1="0" y1="6" x2="32" y2="6" stroke="#10b981" strokeWidth="1.5" strokeDasharray="6 4"/>
              <polygon points="30,3 38,6 30,9" fill="#10b981"/>
            </svg>
            <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>leads to trigger</span>
          </div>
        </div>
      </div>
    </div>
  );
}
/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function WhatsAppBuilder() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isTemplate = window.location.pathname.includes('/template-builder/');
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [botName, setBotName] = useState('My Bot');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);
  const [newTriggerWord, setNewTriggerWord] = useState('');
  const [newTriggerResponse, setNewTriggerResponse] = useState('');
  const [expandedTriggers, setExpandedTriggers] = useState<Set<number>>(new Set([0]));

  const allVariables = triggers.flatMap(t => t.responses.filter(r => r.askVariable).map(r => r.askVariable as string));

  useEffect(() => { loadFlow(); }, [botId]);

  const normalizeTriggers = (rawTriggers: any[]): Trigger[] =>
    (rawTriggers || []).map((t: any) => ({
      ...t,
      responses: (t.responses || []).map((r: any, i: number) => normalizeResponse(r, i)),
    }));

  const loadFlow = async () => {
    if (!botId) { setLoading(false); return; }
    const preloaded = (location.state as any)?.preloadedFlow;
    if (preloaded && !isTemplate) {
      setTriggers(normalizeTriggers(preloaded.triggers));
      setBotName(preloaded.name || 'My Bot');
      setLoading(false);
      return;
    }
    try {
      const snap = await get(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`));
      if (snap.exists()) {
        const flow = snap.val();
        setTriggers(normalizeTriggers(flow.triggers));
        setBotName(flow.name || 'My Bot');
      } else {
        setTriggers([{ trigger: 'hi', responses: [{ id: '1', type: 'text', content: 'Hello! How can I help you?' }] }]);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleExpand = (idx: number) => {
    setExpandedTriggers(prev => {
      const next = new Set(Array.from(prev));
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const addTrigger = () => {
    if (!newTriggerWord.trim()) { alert('Enter a trigger word'); return; }
    const idx = triggers.length;
    setTriggers([...triggers, {
      trigger: newTriggerWord.toLowerCase().trim(),
      responses: [{ id: Date.now().toString(), type: 'text', content: newTriggerResponse || 'New response' }]
    }]);
    setNewTriggerWord('');
    setNewTriggerResponse('');
    setExpandedTriggers(prev => new Set([...Array.from(prev), idx]));
  };

  const deleteTrigger = (idx: number) => {
    if (!window.confirm(`Delete trigger "${triggers[idx].trigger}"?`)) return;
    setTriggers(triggers.filter((_, i) => i !== idx));
  };

  const duplicateTrigger = (idx: number) => {
    const src = triggers[idx];
    const dup = { ...src, trigger: src.trigger + '_copy', responses: src.responses.map(r => ({ ...r, id: Date.now().toString() + Math.random() })) };
    setTriggers([...triggers, dup]);
  };

  const updateTrigger = (idx: number, value: string) => {
    const updated = [...triggers];
    updated[idx].trigger = value.toLowerCase();
    setTriggers(updated);
  };

  const addResponse = (tIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses.push({ id: Date.now().toString(), type: 'text', content: 'New message' });
    setTriggers(updated);
  };

  const deleteResponse = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses = updated[tIdx].responses.filter((_, i) => i !== rIdx);
    setTriggers(updated);
  };

  const updateResponse = (tIdx: number, rIdx: number, field: string, value: any) => {
    const updated = [...triggers];
    if (field.startsWith('listSections.')) {
      const parts = field.split('.');
      const sIdx = parseInt(parts[1]);
      if (parts[2] === 'title') updated[tIdx].responses[rIdx].listSections![sIdx].title = value;
    } else {
      updated[tIdx].responses[rIdx] = { ...updated[tIdx].responses[rIdx], [field]: value };
    }
    setTriggers(updated);
  };

  const moveResponseUp = (tIdx: number, rIdx: number) => {
    if (rIdx === 0) return;
    const updated = [...triggers];
    [updated[tIdx].responses[rIdx - 1], updated[tIdx].responses[rIdx]] = [updated[tIdx].responses[rIdx], updated[tIdx].responses[rIdx - 1]];
    setTriggers(updated);
  };

  const addButton = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    const resp = updated[tIdx].responses[rIdx];
    if (!resp.buttons) resp.buttons = [];
    if (resp.buttons.length >= 3) { alert('Max 3 buttons'); return; }
    resp.buttons.push({ id: Date.now().toString(), title: 'Button', payload: 'payload' });
    setTriggers(updated);
  };

  const updateButton = (tIdx: number, rIdx: number, bIdx: number, field: string, value: string) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].buttons![bIdx] = { ...updated[tIdx].responses[rIdx].buttons![bIdx], [field]: value };
    setTriggers(updated);
  };

  const deleteButton = (tIdx: number, rIdx: number, bIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].buttons = updated[tIdx].responses[rIdx].buttons!.filter((_, i) => i !== bIdx);
    setTriggers(updated);
  };

  const addListSection = (tIdx: number, rIdx: number) => {
    const updated = [...triggers];
    if (!updated[tIdx].responses[rIdx].listSections) updated[tIdx].responses[rIdx].listSections = [];
    updated[tIdx].responses[rIdx].listSections!.push({ title: 'New Section', rows: [] });
    setTriggers(updated);
  };

  const addListRow = (tIdx: number, rIdx: number, sIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].listSections![sIdx].rows.push({ id: Date.now().toString(), title: 'New Option', payload: 'option_payload' });
    setTriggers(updated);
  };

  const updateListRow = (tIdx: number, rIdx: number, sIdx: number, rowIdx: number, field: string, value: string) => {
    const updated = [...triggers];
    (updated[tIdx].responses[rIdx].listSections![sIdx].rows[rowIdx] as any)[field] = value;
    setTriggers(updated);
  };

  const deleteListRow = (tIdx: number, rIdx: number, sIdx: number, rowIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].listSections![sIdx].rows = updated[tIdx].responses[rIdx].listSections![sIdx].rows.filter((_, i) => i !== rowIdx);
    setTriggers(updated);
  };

  const deleteListSection = (tIdx: number, rIdx: number, sIdx: number) => {
    const updated = [...triggers];
    updated[tIdx].responses[rIdx].listSections = updated[tIdx].responses[rIdx].listSections!.filter((_, i) => i !== sIdx);
    setTriggers(updated);
  };

  const saveFlow = async () => {
    if (!auth.currentUser) { alert('Login required'); return; }
    setSaving(true);
    try {
      const cleanTriggers = triggers.map(t => ({
        trigger: t.trigger,
        responses: t.responses.map((r, rIdx) => {
          const clean = cleanResponseItem(r, rIdx);
          if (r.type === 'order_handler' && r.orderHandlerConfig) clean.orderHandlerConfig = r.orderHandlerConfig;
          if (r.type === 'dynamic_list') {
            if (r.apiUrl) clean.apiUrl = r.apiUrl;
            if (r.apiMethod) clean.apiMethod = r.apiMethod;
            if (r.apiHeaders?.length) clean.apiHeaders = r.apiHeaders;
            if (r.apiBody) clean.apiBody = r.apiBody;
            if (r.itemTitlePath) clean.itemTitlePath = r.itemTitlePath;
            if (r.itemPayloadPath) clean.itemPayloadPath = r.itemPayloadPath;
            if (r.listTitle) clean.listTitle = r.listTitle;
          }
          if (r.type === 'dynamic_response' && r.dynamicResponseConfig) {
            const cfg = r.dynamicResponseConfig;
            const cc: any = {};
            if (cfg.customFields?.length) cc.customFields = cfg.customFields;
            if (cfg.sourceListTrigger) cc.sourceListTrigger = cfg.sourceListTrigger;
            if (cfg.apiUrl) cc.apiUrl = cfg.apiUrl;
            if (cfg.apiMethod) cc.apiMethod = cfg.apiMethod;
            if (cfg.apiHeaders?.length) cc.apiHeaders = cfg.apiHeaders.filter(h => h.key && h.value);
            if (cfg.apiBody) cc.apiBody = cfg.apiBody;
            if (cfg.imagePath) cc.imagePath = cfg.imagePath;
            if (cfg.titlePath) cc.titlePath = cfg.titlePath;
            if (cfg.categoryPath) cc.categoryPath = cfg.categoryPath;
            if (cfg.pricePath) cc.pricePath = cfg.pricePath;
            if (cfg.descriptionPath) cc.descriptionPath = cfg.descriptionPath;
            if (cfg.metaPath) cc.metaPath = cfg.metaPath;
            if (cfg.metaLabel) cc.metaLabel = cfg.metaLabel;
            if (cfg.button1Title && cfg.button1Payload) { cc.button1Title = cfg.button1Title; cc.button1Payload = cfg.button1Payload; }
            if (cfg.button2Title && cfg.button2Payload) { cc.button2Title = cfg.button2Title; cc.button2Payload = cfg.button2Payload; }
            if (cfg.button3Title && cfg.button3Payload) { cc.button3Title = cfg.button3Title; cc.button3Payload = cfg.button3Payload; }
            if (cfg.fallbackMessage) cc.fallbackMessage = cfg.fallbackMessage;
            if (cc.titlePath && cc.apiUrl) clean.dynamicResponseConfig = cc;
          }
          return clean;
        })
      }));

      await set(ref(db, isTemplate ? `templates/${botId}` : `flows/${botId}`), { name: botName, triggers: cleanTriggers, botId, userId: auth.currentUser.uid, updatedAt: Date.now() });
      alert('Saved!');
    } catch (e) {
      alert('Error saving: ' + (e instanceof Error ? e.message : e));
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (tmpl: any) => {
    setTriggers(normalizeTriggers(tmpl.triggers));
    if (tmpl.name && !botName) setBotName(tmpl.name);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-page)' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading flow...</div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════ */
  return (
    <>
      <SEO title={`Edit Flow | ${botName}`} description="WhatsApp bot flow builder" keywords="" type="website" noindex />
      {showFlowDiagram && <FlowDiagramModal triggers={triggers} botName={botName} onClose={() => setShowFlowDiagram(false)} />}
      <div style={S.page}>

        {/* Top bar */}
        <div style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--bg-element)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => navigate(isTemplate ? '/templates' : '/dashboard')} style={{ ...S.btnGhost, display: 'flex', alignItems: 'center', gap: '6px' }}>
              ← {isTemplate ? 'Templates' : 'Dashboard'}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>💬</span>
              <input
                value={botName}
                onChange={e => setBotName(e.target.value)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '16px', fontWeight: '700', outline: 'none', minWidth: '200px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>{triggers.length} triggers</div>
            <button onClick={() => setShowFlowDiagram(true)} style={{ ...S.btnGhost, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              🗺️ View Flow
            </button>
            <TemplateSelector botType="whatsapp" onApply={applyTemplate} />
            {!isTemplate && <SaveAsTemplate botType="whatsapp" triggers={triggers} />}
            <button onClick={saveFlow} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : '💾 Save flow'}
            </button>
          </div>
        </div>

        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '28px 20px' }}>

          {/* Add Trigger */}
          <div style={{ ...S.card, borderStyle: 'dashed', borderColor: '#5B4FE840' }}>
            <div style={{ color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', marginBottom: '12px' }}>Add a new trigger</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>When user types</label>
                <input
                  value={newTriggerWord}
                  onChange={e => setNewTriggerWord(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTrigger()}
                  placeholder="e.g. booking, prices, hi"
                  style={S.input}
                />
              </div>
              <div style={{ flex: 2 }}>
                <label style={S.label}>First response</label>
                <input
                  value={newTriggerResponse}
                  onChange={e => setNewTriggerResponse(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTrigger()}
                  placeholder="e.g. Here are our prices..."
                  style={S.input}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={addTrigger} style={S.btnPrimary}>+ Add</button>
              </div>
            </div>
          </div>

          {/* Trigger list */}
          {triggers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-faint)', fontSize: '14px' }}>
              No triggers yet. Add one above to get started.
            </div>
          )}

          {triggers.map((trigger, tIdx) => {
            const isExpanded = expandedTriggers.has(tIdx);
            return (
              <div key={tIdx} style={S.card}>
                {/* Trigger header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => toggleExpand(tIdx)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', padding: '0', flexShrink: 0 }}
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>

                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Trigger word</label>
                    <input
                      value={trigger.trigger}
                      onChange={e => updateTrigger(tIdx, e.target.value)}
                      style={{ ...S.input, fontWeight: '600', color: '#818cf8' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginTop: '18px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-faint)', padding: '4px 10px', background: 'var(--bg-element)', borderRadius: '10px' }}>
                      {trigger.responses.length} response{trigger.responses.length !== 1 ? 's' : ''}
                    </span>
                    <button onClick={() => duplicateTrigger(tIdx)} style={S.btnGhost} title="Duplicate">📋</button>
                    <button onClick={() => deleteTrigger(tIdx)} style={S.btnDanger}>Delete</button>
                  </div>
                </div>

                {/* Responses */}
                {isExpanded && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600' }}>RESPONSES (sent in order)</div>
                      <button onClick={() => addResponse(tIdx)} style={S.btnGreen}>+ Add response</button>
                    </div>

                    {trigger.responses.map((resp, rIdx) => (
                      <ResponseEditor
                        key={resp.id}
                        resp={resp}
                        rIdx={rIdx}
                        tIdx={tIdx}
                        allVariables={allVariables}
                        triggers={triggers}
                        onUpdate={(field, value) => updateResponse(tIdx, rIdx, field, value)}
                        onDelete={() => deleteResponse(tIdx, rIdx)}
                        onMoveUp={() => moveResponseUp(tIdx, rIdx)}
                        onAddButton={() => addButton(tIdx, rIdx)}
                        onUpdateButton={(bIdx, field, value) => updateButton(tIdx, rIdx, bIdx, field, value)}
                        onDeleteButton={(bIdx) => deleteButton(tIdx, rIdx, bIdx)}
                        onAddListSection={() => addListSection(tIdx, rIdx)}
                        onAddListRow={(sIdx) => addListRow(tIdx, rIdx, sIdx)}
                        onUpdateListRow={(sIdx, rowIdx, field, value) => updateListRow(tIdx, rIdx, sIdx, rowIdx, field, value)}
                        onDeleteListRow={(sIdx, rowIdx) => deleteListRow(tIdx, rIdx, sIdx, rowIdx)}
                        onDeleteListSection={(sIdx) => deleteListSection(tIdx, rIdx, sIdx)}
                        onUpdateAskResponse={(fIdx, field, value) => {
                          const updated = [...triggers];
                          (updated[tIdx].responses[rIdx].askResponses![fIdx] as any)[field] = value;
                          setTriggers(updated);
                        }}
                        onAddAskResponse={() => {
                          const updated = [...triggers];
                          if (!updated[tIdx].responses[rIdx].askResponses) updated[tIdx].responses[rIdx].askResponses = [];
                          updated[tIdx].responses[rIdx].askResponses!.push({ id: Date.now().toString(), type: 'text', content: 'Thank you!' });
                          setTriggers(updated);
                        }}
                        onDeleteAskResponse={(fIdx) => {
                          const updated = [...triggers];
                          updated[tIdx].responses[rIdx].askResponses = updated[tIdx].responses[rIdx].askResponses!.filter((_, i) => i !== fIdx);
                          setTriggers(updated);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   RESPONSE EDITOR (extracted component)
───────────────────────────────────────────── */
interface ResponseEditorProps {
  resp: ResponseItem; rIdx: number; tIdx: number;
  allVariables: string[]; triggers: Trigger[];
  onUpdate: (field: string, value: any) => void;
  onDelete: () => void; onMoveUp: () => void;
  onAddButton: () => void;
  onUpdateButton: (bIdx: number, field: string, value: string) => void;
  onDeleteButton: (bIdx: number) => void;
  onAddListSection: () => void;
  onAddListRow: (sIdx: number) => void;
  onUpdateListRow: (sIdx: number, rowIdx: number, field: string, value: string) => void;
  onDeleteListRow: (sIdx: number, rowIdx: number) => void;
  onDeleteListSection: (sIdx: number) => void;
  onUpdateAskResponse: (fIdx: number, field: string, value: any) => void;
  onAddAskResponse: () => void;
  onDeleteAskResponse: (fIdx: number) => void;
}

function ResponseEditor({
  resp, rIdx, tIdx, allVariables, triggers,
  onUpdate, onDelete, onMoveUp,
  onAddButton, onUpdateButton, onDeleteButton,
  onAddListSection, onAddListRow, onUpdateListRow, onDeleteListRow, onDeleteListSection,
  onUpdateAskResponse, onAddAskResponse, onDeleteAskResponse,
}: ResponseEditorProps) {

  const typeColors: Record<string, string> = {
    text: '#10b981', image: '#3b82f6', document: '#8b5cf6',
    buttons: '#f59e0b', list: '#6366f1', webhook: '#ef4444',
    ask: '#ec4899', chain: '#14b8a6', dynamic_list: '#0ea5e9',
    dynamic_response: '#f97316', order_handler: '#84cc16',
  };
  const color = typeColors[resp.type] || '#64748b';

  return (
    <div style={{ ...S.respCard, borderLeft: `3px solid ${color}` }}>
      {/* Response header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ background: color + '20', color, fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px' }}>
            #{rIdx + 1}
          </span>
          <select
            value={resp.type}
            onChange={e => onUpdate('type', e.target.value)}
            style={{ ...S.select, width: 'auto', padding: '5px 10px', fontSize: '12px', color }}
          >
            <option value="text">💬 Text</option>
            <option value="image">🖼️ Image</option>
            <option value="document">📄 Document</option>
            <option value="buttons">🔘 Buttons</option>
            <option value="list">📋 List</option>
            <option value="webhook">🌐 Webhook</option>
            <option value="ask">❓ Ask question</option>
            <option value="chain">🔗 Chain</option>
            <option value="dynamic_list">📡 Dynamic list</option>
            <option value="dynamic_response">🔗 Dynamic response</option>
            <option value="order_handler">🛒 Order handler</option>
            <option value="request_agent">🙋 Talk to a Human</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {rIdx > 0 && <button onClick={onMoveUp} style={S.btnGhost}>↑ Up</button>}
          <button onClick={onDelete} style={S.btnDanger}>Delete</button>
        </div>
      </div>

      {/* ── TEXT ── */}
      {resp.type === 'text' && (
        <div>
          <textarea
            value={resp.content}
            onChange={e => onUpdate('content', e.target.value)}
            rows={3}
            placeholder="Message text. Use {{variableName}} to insert collected data."
            style={S.textarea}
          />
          {allVariables.length > 0 && (
            <div style={{ marginTop: '8px', padding: '10px', background: '#10b98110', borderRadius: '8px', border: '1px solid #10b98130' }}>
              <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '6px', fontWeight: '600' }}>
                Collected variables ({resp.content?.length || 0} chars)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {allVariables.map(v => (
                  <button key={v} onClick={() => onUpdate('content', resp.content + `{{${v}}}`)}
                    style={{ padding: '3px 8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '11px' }}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── REQUEST AGENT ── */}
      {resp.type === 'request_agent' && (
        <div>
          <textarea
            value={resp.content}
            onChange={e => onUpdate('content', e.target.value)}
            rows={2}
            placeholder='Confirmation message, e.g. "Connecting you to a team member..."'
            style={{ ...S.textarea, marginBottom: '8px' }}
          />
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Sends this message, then pauses the bot for this conversation and notifies you — the same handoff the dashboard's "Take Over" button uses. Reply from the Conversations tab when you're ready.
          </div>
        </div>
      )}

      {/* ── IMAGE / DOCUMENT ── */}
      {(resp.type === 'image' || resp.type === 'document') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <MediaPicker value={resp.content} onChange={url => onUpdate('content', url)} kind={resp.type === 'image' ? 'image' : 'document'} placeholder="URL" />
          <input value={resp.caption || ''} onChange={e => onUpdate('caption', e.target.value)} placeholder="Caption (optional)" style={S.input} />
          {resp.type === 'document' && <input value={resp.filename || ''} onChange={e => onUpdate('filename', e.target.value)} placeholder="Filename" style={S.input} />}
        </div>
      )}

      {/* ── BUTTONS ── */}
      {resp.type === 'buttons' && (
        <div>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2} placeholder="Message above buttons" style={{ ...S.textarea, marginBottom: '12px' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={S.label}>Buttons (max 3)</label>
            <button onClick={onAddButton} disabled={resp.buttons && resp.buttons.length >= 3} style={{ ...S.btnGhost, opacity: resp.buttons && resp.buttons.length >= 3 ? 0.4 : 1 }}>+ Add</button>
          </div>
          {resp.buttons?.map((btn, bIdx) => (
            <div key={btn.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input value={btn.title} onChange={e => onUpdateButton(bIdx, 'title', e.target.value)} placeholder="Button text" style={{ ...S.input, flex: 1 }} />
              <input value={btn.payload} onChange={e => onUpdateButton(bIdx, 'payload', e.target.value)} placeholder="Payload (trigger word)" style={{ ...S.input, flex: 1 }} />
              <button onClick={() => onDeleteButton(bIdx)} style={S.btnDanger}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ── CHAIN ── */}
      {resp.type === 'chain' && (
        <div style={{ background: '#14b8a610', border: '1px solid #14b8a630', borderRadius: '8px', padding: '12px' }}>
          <div style={{ color: '#14b8a6', fontSize: '12px', fontWeight: '600', marginBottom: '10px' }}>Auto-triggers next flow after delay</div>
          <input value={resp.chainTrigger || ''} onChange={e => onUpdate('chainTrigger', e.target.value)} placeholder="Trigger word to auto-send" style={{ ...S.input, marginBottom: '8px' }} />
          <input type="number" value={resp.chainDelay || 1} onChange={e => onUpdate('chainDelay', parseInt(e.target.value) || 1)} placeholder="Delay in seconds" style={S.input} />
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
            Bot sends "{resp.chainTrigger || 'trigger'}" automatically after {resp.chainDelay || 1}s
          </div>
        </div>
      )}

      {/* ── LIST ── */}
      {resp.type === 'list' && (
        <div>
          <textarea value={resp.content} onChange={e => onUpdate('content', e.target.value)} rows={2} placeholder="Message above list" style={{ ...S.textarea, marginBottom: '12px' }} />
          <button onClick={onAddListSection} style={{ ...S.btnGhost, marginBottom: '12px' }}>+ Add section</button>
          {resp.listSections?.map((section, sIdx) => (
            <div key={sIdx} style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px', marginBottom: '10px', border: '1px solid var(--bg-element)' }}>
              <input value={section.title} onChange={e => onUpdate(`listSections.${sIdx}.title`, e.target.value)} placeholder="Section title" style={{ ...S.input, marginBottom: '10px', fontWeight: '600' }} />
              {section.rows.map((row, rowIdx) => (
                <div key={row.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 2 }}>
                    <input
                      value={row.title}
                      onChange={e => onUpdateListRow(sIdx, rowIdx, 'title', e.target.value.slice(0, 24))}
                      placeholder="Option text (max 24 chars)"
                      style={S.input}
                    />
                    <div style={{ fontSize: '10px', color: row.title.length > 20 ? '#f59e0b' : 'var(--text-faint)', marginTop: '2px' }}>
                      {row.title.length}/24
                    </div>
                  </div>
                  <input value={row.payload} onChange={e => onUpdateListRow(sIdx, rowIdx, 'payload', e.target.value)} placeholder="Payload" style={{ ...S.input, flex: 2 }} />
                  <button onClick={() => onDeleteListRow(sIdx, rowIdx)} style={S.btnDanger}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button onClick={() => { if (section.rows.length >= 10) { alert('Max 10 rows'); return; } onAddListRow(sIdx); }} style={S.btnGhost}>+ Add option</button>
                <button onClick={() => onDeleteListSection(sIdx)} style={S.btnDanger}>Delete section</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── WEBHOOK ── */}
      {resp.type === 'webhook' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={resp.content} onChange={e => onUpdate('content', e.target.value)} placeholder="API URL" style={S.input} />
          <select value={resp.webhookMethod || 'GET'} onChange={e => onUpdate('webhookMethod', e.target.value)} style={S.select}>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
          <textarea value={resp.webhookBody || ''} onChange={e => onUpdate('webhookBody', e.target.value)} rows={2} placeholder="POST Body (JSON)" style={S.textarea} />
          <input value={resp.webhookResponsePath || ''} onChange={e => onUpdate('webhookResponsePath', e.target.value)} placeholder="Response path (e.g. status)" style={S.input} />
          <input value={resp.webhookSuccessMessage || ''} onChange={e => onUpdate('webhookSuccessMessage', e.target.value)} placeholder="Success message" style={S.input} />
          <input value={resp.webhookErrorMessage || ''} onChange={e => onUpdate('webhookErrorMessage', e.target.value)} placeholder="Error message" style={S.input} />
        </div>
      )}

      {/* ── ASK ── */}
      {resp.type === 'ask' && (
        <div>
          <input value={resp.content} onChange={e => onUpdate('content', e.target.value)} placeholder="Question to ask e.g. What is your name?" style={{ ...S.input, marginBottom: '8px' }} />
          <input value={resp.askVariable || ''} onChange={e => onUpdate('askVariable', e.target.value)} placeholder="Variable name (e.g. name, phone, email)" style={{ ...S.input, marginBottom: '8px' }} />
          {resp.askVariable && (
            <div style={{ fontSize: '11px', color: '#f59e0b', padding: '8px 12px', background: '#f59e0b10', borderRadius: '8px', marginBottom: '12px' }}>
              Answer saved as <strong>{`{{${resp.askVariable}}}`}</strong> — use this in any Text response below
            </div>
          )}
          <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>AFTER ANSWER, SEND:</div>
          <button onClick={onAddAskResponse} style={{ ...S.btnGhost, marginBottom: '10px' }}>+ Add follow-up response</button>
          {resp.askResponses?.map((follow, fIdx) => (
            <div key={follow.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <select value={follow.type} onChange={e => onUpdateAskResponse(fIdx, 'type', e.target.value)} style={{ ...S.select, flex: 1, width: 'auto' }}>
                <option value="text">Text</option>
                <option value="buttons">Buttons</option>
                <option value="list">List</option>
              </select>
              <input value={follow.content} onChange={e => onUpdateAskResponse(fIdx, 'content', e.target.value)} placeholder="Response text" style={{ ...S.input, flex: 2 }} />
              <button onClick={() => onDeleteAskResponse(fIdx)} style={S.btnDanger}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ── DYNAMIC LIST ── */}
      {resp.type === 'dynamic_list' && (
        <div style={{ background: '#0ea5e910', border: '1px solid #0ea5e930', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ color: '#0ea5e9', fontSize: '12px', fontWeight: '600' }}>Fetches items from an API and shows as a list</div>
          <input value={resp.apiUrl || ''} onChange={e => onUpdate('apiUrl', e.target.value)} placeholder="API URL (e.g. https://api.example.com/products)" style={S.input} />
          <select value={resp.apiMethod || 'GET'} onChange={e => onUpdate('apiMethod', e.target.value)} style={S.select}>
            <option value="GET">GET</option><option value="POST">POST</option>
          </select>
          {resp.apiMethod === 'POST' && <textarea value={resp.apiBody || ''} onChange={e => onUpdate('apiBody', e.target.value)} rows={2} placeholder="POST Body (JSON)" style={S.textarea} />}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <input value={resp.itemTitlePath || ''} onChange={e => onUpdate('itemTitlePath', e.target.value)} placeholder="Title path (e.g. name)" style={S.input} />
            <input value={resp.itemPayloadPath || ''} onChange={e => onUpdate('itemPayloadPath', e.target.value)} placeholder="Payload path (e.g. id)" style={S.input} />
          </div>
          <input value={resp.listTitle || ''} onChange={e => onUpdate('listTitle', e.target.value)} placeholder="List button title (e.g. Products)" style={S.input} />
          <textarea value={resp.content || ''} onChange={e => onUpdate('content', e.target.value)} rows={2} placeholder="Message above the list" style={S.textarea} />
        </div>
      )}

      {/* ── DYNAMIC RESPONSE ── */}
      {resp.type === 'dynamic_response' && (
        <DynamicResponseEditor resp={resp} triggers={triggers} onUpdate={onUpdate} />
      )}

      {/* ── ORDER HANDLER ── */}
      {resp.type === 'order_handler' && (
        <OrderHandlerEditor resp={resp} onUpdate={onUpdate} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   DYNAMIC RESPONSE EDITOR
───────────────────────────────────────────── */
function DynamicResponseEditor({ resp, triggers, onUpdate }: { resp: ResponseItem; triggers: Trigger[]; onUpdate: (f: string, v: any) => void }) {
  const cfg = resp.dynamicResponseConfig || getDefaultDynamicResponseConfig();
  const update = (field: string, value: any) => onUpdate('dynamicResponseConfig', { ...cfg, [field]: value });

  return (
    <div style={{ background: '#f9731610', border: '1px solid #f9731630', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ color: '#f97316', fontSize: '12px', fontWeight: '600' }}>Fetches and displays details of a selected item</div>

      <div>
        <label style={S.label}>Connected list trigger</label>
        <select value={cfg.sourceListTrigger || ''} onChange={e => update('sourceListTrigger', e.target.value)} style={S.select}>
          <option value="">Select a dynamic list...</option>
          {triggers.flatMap(t => t.responses.filter(r => r.type === 'dynamic_list').map((_, idx) => (
            <option key={idx} value={t.trigger}>{t.trigger} → Dynamic list</option>
          )))}
        </select>
      </div>

      <input value={cfg.apiUrl || ''} onChange={e => update('apiUrl', e.target.value)} placeholder="API URL (e.g. https://api.example.com/item/{{id}})" style={S.input} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <select value={cfg.apiMethod || 'GET'} onChange={e => update('apiMethod', e.target.value)} style={S.select}>
          <option value="GET">GET</option><option value="POST">POST</option>
        </select>
        <input value={cfg.apiBody || ''} onChange={e => update('apiBody', e.target.value)} placeholder="POST body (JSON)" style={S.input} />
      </div>

      <div style={{ borderTop: '1px solid var(--bg-element)', paddingTop: '10px' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>MESSAGE FORMAT</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <input value={cfg.imagePath || ''} onChange={e => update('imagePath', e.target.value)} placeholder="Image path (optional)" style={S.input} />
          <input value={cfg.titlePath || ''} onChange={e => update('titlePath', e.target.value)} placeholder="Title path *" style={S.input} />
          <input value={cfg.categoryPath || ''} onChange={e => update('categoryPath', e.target.value)} placeholder="Category path (optional)" style={S.input} />
          <input value={cfg.metaPath || ''} onChange={e => update('metaPath', e.target.value)} placeholder="Meta path (price/date/rating)" style={S.input} />
          <input value={cfg.metaLabel || ''} onChange={e => update('metaLabel', e.target.value)} placeholder="Meta label (e.g. Price)" style={S.input} />
          <input value={cfg.descriptionPath || ''} onChange={e => update('descriptionPath', e.target.value)} placeholder="Description path *" style={S.input} />
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '600' }}>EXTRA FIELDS</div>
          <button onClick={() => {
            const fields = [...(cfg.customFields || []), { id: Date.now().toString(), label: 'New Field', path: '', emoji: '📌' }];
            update('customFields', fields);
          }} style={S.btnGhost}>+ Add field</button>
        </div>
        {(cfg.customFields || []).map((field, fIdx) => (
          <div key={field.id} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <input value={field.emoji || '📌'} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx].emoji = e.target.value; update('customFields', f); }} style={{ ...S.input, width: '50px' }} />
            <input value={field.label} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx].label = e.target.value; update('customFields', f); }} placeholder="Label" style={{ ...S.input, flex: 1 }} />
            <input value={field.path} onChange={e => { const f = [...(cfg.customFields || [])]; f[fIdx].path = e.target.value; update('customFields', f); }} placeholder="JSON path" style={{ ...S.input, flex: 2 }} />
            <button onClick={() => { update('customFields', (cfg.customFields || []).filter((_, i) => i !== fIdx)); }} style={S.btnDanger}>✕</button>
          </div>
        ))}
      </div>

      <div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>BUTTONS (max 3)</div>
        {[1, 2, 3].map(n => (
          <div key={n} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
            <input value={(cfg as any)[`button${n}Title`] || ''} onChange={e => update(`button${n}Title`, e.target.value)} placeholder={`Button ${n} text`} style={S.input} />
            <input value={(cfg as any)[`button${n}Payload`] || ''} onChange={e => update(`button${n}Payload`, e.target.value)} placeholder={`Payload (use {{id}})`} style={S.input} />
          </div>
        ))}
      </div>

      <textarea value={cfg.fallbackMessage || ''} onChange={e => update('fallbackMessage', e.target.value)} rows={2} placeholder="Fallback message if no data found" style={S.textarea} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ORDER HANDLER EDITOR
───────────────────────────────────────────── */
function OrderHandlerEditor({ resp, onUpdate }: { resp: ResponseItem; onUpdate: (f: string, v: any) => void }) {
  const cfg = resp.orderHandlerConfig || { collectName: false, collectPhone: false, collectEmail: false, collectAddress: false, askQuantity: false, action: 'custom_message' as const, confirmationMessage: '' };
  const update = (changes: Partial<typeof cfg>) => onUpdate('orderHandlerConfig', { ...cfg, ...changes });

  return (
    <div style={{ background: '#84cc1610', border: '1px solid #84cc1630', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ color: '#84cc16', fontSize: '12px', fontWeight: '600' }}>Collect customer info and process orders</div>

      <div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>COLLECT FROM CUSTOMER</div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {(['collectName', 'collectPhone', 'collectEmail', 'collectAddress'] as const).map(field => (
            <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={cfg[field] || false} onChange={e => update({ [field]: e.target.checked })} />
              {field.replace('collect', '')}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.askQuantity || false} onChange={e => update({ askQuantity: e.target.checked })} />
          Ask for quantity
        </label>
        {cfg.askQuantity && (
          <input value={cfg.quantityLabel || ''} onChange={e => update({ quantityLabel: e.target.value })} placeholder="Question (e.g. How many would you like?)" style={{ ...S.input, marginTop: '8px' }} />
        )}
      </div>

      <div>
        <label style={S.label}>After collection</label>
        <select value={cfg.action || 'custom_message'} onChange={e => update({ action: e.target.value as any })} style={S.select}>
          <option value="payment_link">Generate payment link</option>
          <option value="admin_notification">Notify admin</option>
          <option value="api_call">Call API</option>
          <option value="custom_message">Send custom message</option>
        </select>
      </div>

      {cfg.action === 'payment_link' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={cfg.paymentLinkUrl || ''} onChange={e => update({ paymentLinkUrl: e.target.value })} placeholder="Payment URL (use {{id}}, {{price}}, {{name}})" style={S.input} />
          <input value={cfg.paymentLinkLabel || ''} onChange={e => update({ paymentLinkLabel: e.target.value })} placeholder="Button text (e.g. Pay Now)" style={S.input} />
        </div>
      )}

      {cfg.action === 'admin_notification' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={cfg.adminPhone || ''} onChange={e => update({ adminPhone: e.target.value })} placeholder="Admin WhatsApp number (e.g. 263712345678)" style={S.input} />
          <textarea value={cfg.adminMessage || ''} onChange={e => update({ adminMessage: e.target.value })} rows={2} placeholder="Admin message (use {{product}}, {{name}}, etc.)" style={S.textarea} />
        </div>
      )}

      {cfg.action === 'api_call' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input value={cfg.apiUrl || ''} onChange={e => update({ apiUrl: e.target.value })} placeholder="API URL" style={S.input} />
          <select value={cfg.apiMethod || 'POST'} onChange={e => update({ apiMethod: e.target.value as any })} style={S.select}>
            <option value="POST">POST</option><option value="GET">GET</option>
          </select>
          <textarea value={cfg.apiBody || ''} onChange={e => update({ apiBody: e.target.value })} rows={2} placeholder="POST body (JSON) — use {{id}}, {{name}}, {{phone}}" style={S.textarea} />
          <input value={cfg.successMessage || ''} onChange={e => update({ successMessage: e.target.value })} placeholder="Success message" style={S.input} />
          <input value={cfg.errorMessage || ''} onChange={e => update({ errorMessage: e.target.value })} placeholder="Error message" style={S.input} />
        </div>
      )}

      {cfg.action === 'custom_message' && (
        <textarea value={cfg.customMessage || ''} onChange={e => update({ customMessage: e.target.value })} rows={2} placeholder="Custom message to send after collection" style={S.textarea} />
      )}

      <div>
        <label style={S.label}>Final confirmation message</label>
        <textarea value={cfg.confirmationMessage || ''} onChange={e => update({ confirmationMessage: e.target.value })} rows={2} placeholder="Thanks for your order! We'll be in touch shortly." style={S.textarea} />
      </div>
    </div>
  );
}
